import { defineStore } from "pinia";
import { chatBotManager } from "@/services/chatbot/chatbot-manager";

export const useChatbotStore = defineStore({
  id: "Chatbot",
  state: () => {
    return {
      messages: [
        {
          content: "Bonjour, en quoi puis-je vous aider ?",
          writer: "chatbot",
        },
      ],
      userSecretKey: null,
      loading: false,
      canStop: false,
      listen: false,
      controller: null,
      intervals: {},
    };
  },

  actions: {
    storeMessage(message, container) {
      this.messages.push(message);
      this.scrollToBottom(container);
    },
    clear() {
      this.messages = [];
    },
    async chatbotRequest(message, container, model, user, roles) {
      this.loading = true;
      const url = process.env.VUE_APP_CHATBOT_SERVEUR_URL + "/api/chatbot";

      if (!chatBotManager.isToken() || !chatBotManager.isValid()) {
        await chatBotManager.login();
      }

      if (!this.userSecretKey && sessionStorage.getItem("studea-userSecretKey")) {
        this.userSecretKey = sessionStorage.getItem("studea-userSecretKey");
      }

      const params = {
        message: message.content,
        user_secret_key: this.userSecretKey,
        user_id: user.id,
        user_name: user.fullName,
        url_api: this.getApiPath(),
        roles: roles,
      };

      this.controller = new AbortController();

      const response = await fetch(url, {
        method: "POST",
        signal: this.controller.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: "Bearer " + chatBotManager.getToken(),
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(
          `generateImage HTTP error! status: ${response.status} ${response.statusText}. Details: ${JSON.stringify(errorBody)}`,
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      this.listen = true;
      let buffer = "";
      while (this.listen) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        buffer = parts.pop();

        for (const part of parts) {
          if (part.startsWith("data:")) {
            const jsonStr = part.replace(/^data:\s*/, "");
            try {
              const res = JSON.parse(jsonStr);
              if (res && res.response_status_code === 200) {
                const content = {
                  content: "",
                  response: res.response,
                  writer: "Chatbot",
                  id: Date.now().toString(36) + Math.random().toString(36).slice(2),
                };
                this.userSecretKey = res.user_secret_key;
                sessionStorage.setItem("studea-userSecretKey", res.user_secret_key);
                this.storeMessage(content);
                this.write(content, container, model);
                if (res.request_finished) {
                  this.loading = false;
                  this.listen = false;
                }
              }
            } catch (err) {
              console.error("Erreur JSON :", err, jsonStr);
            }
          }
        }
      }
    },

    setCanStop(value) {
      this.canStop = value;
    },

    write(message, container, model, suggestions = false) {
      let start = 0;

      this.intervals[message.id] = setInterval(() => {
        const char = message.response[start];
        message["typing"] = true;

        if (char === "<") {
          start = message.response.indexOf(">", start);
        }

        const content = message.response.slice(0, start);
        this.writeContent(message.id, content);
        this.scrollToBottom(container);
        if (++start > message.response.length) {
          this.cancelWriting(message);
          if (!suggestions) {
            model.vars.disabled = false;
            this.setCanStop(false);
            this.focusInput();
          } else {
            model.vars.disabled = true;
            this.setCanStop(true);
          }
        }
      }, this.intervalLength);
    },

    stop(model) {
      this.listen = false;
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      const messageTypings = this.messages.filter((message) => message.typing);
      if (messageTypings) {
        messageTypings.forEach((message) => {
          this.cancelWriting(message);
        });
      }
      this.loading = false;
      this.canStop = false;
      model.vars.disabled = false;
    },

    focusInput() {
      const input = document.getElementById("user_chatbot");
      setTimeout(() => {
        input.focus();
      }, 50);
    },

    cancelWriting(message) {
      message["typing"] = true;
      clearInterval(this.intervals[message.id]);
      delete this.intervals[message.id];
    },

    scrollToBottom(element) {
      if (element) {
        setTimeout(function () {
          element.scroll({
            top: element.scrollHeight,
            behavior: "smooth",
          });
        }, 5);
      }
    },

    chatbotLoading() {
      this.loading = true;
    },

    chatbotSuccess() {
      this.loading = false;
    },

    writeContent(id, value) {
      const message = this.messages.find((message) => message.id === id);
      if (message) {
        message.content = value;
      }
    },

    init() {
      this.messages = [
        {
          content: "Bonjour, en quoi puis-je vous aider ?",
          writer: "chatbot",
        },
      ];
      this.loading = false;
    },

    async loadUserHistoric(container) {
      this.loading = true;
      if (!chatBotManager.isToken() || !chatBotManager.isValid()) {
        await chatBotManager.login();
      }

      if (!this.userSecretKey && sessionStorage.getItem("studea-userSecretKey")) {
        this.userSecretKey = sessionStorage.getItem("studea-userSecretKey");
      }
      const url =
        process.env.VUE_APP_CHATBOT_SERVEUR_URL +
        "/api/paginated_history?user_secret_key=" +
        this.userSecretKey +
        "&page=1&limit=15";
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + chatBotManager.getToken(),
        },
      });

      const data = await res.json();

      this.init();
      if (data.history && Object.keys(data.history).length) {
        Object.values(data.history).forEach((item) => {
          this.storeMessage({ content: item.message, writer: "me" });
          if (item.response) {
            this.storeMessage({ content: item.response, writer: "Chatbot" });
          }
        });
      }

      this.scrollToBottom(container);
      this.loading = false;
      return true;
    },

    getApiPath() {
      return window.localStorage.getItem("studea-apiPath")
        ? window.localStorage.getItem("studea-apiPath")
        : window.sessionStorage.getItem("studea-apiPath");
    },
  },
});
